import {
    Modal, Table, Tooltip, Typography, notification,
} from 'antd';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDelete } from 'react-icons/md';
import { BiEdit, BiHelpCircle, BiLinkExternal } from 'react-icons/bi';
import { LibraryContext } from '../context/libraryContext';
import { IDocument } from '../../../interfaces';
import Loading from '../../../widgets/Loading';
import EmptyData from '../../../widgets/EmptyData';
import { renderCategory } from '../../../utils/category';

interface Props {
    t: (key: string) => string;
}

const { Text } = Typography;

const TableLibrary = ({ t }: Props) => {
    const {
        loading, documents, total, paginator, searchParams, onPageChange, fetchDocuments, deleteDocument, errors, actionLoading,
    } = useContext(LibraryContext);

    const { limit, page } = paginator;
    const { search, category } = searchParams;

    const navigate = useNavigate();

    useEffect(() => {
        fetchDocuments();
    }, [paginator, search, category]);

    const confirmDelete = (title: string, documentID: string) => {
        Modal.confirm({
            className: 'modal__confirm-delete',
            icon: <MdDelete size={28} className="modal__confirm-delete icon" />,
            title: t('DELETE_CONFIRMATION'),
            content: title,
            okText: t('DELETE'),
            cancelText: t('CANCEL'),
            onOk: () => {
                deleteDocument(documentID);
                if (errors.length === 0) {
                    notification.success({ message: t('DELETE_SUCCESS') });
                } else {
                    notification.error({
                        message: t('DELETE_ERROR'),
                        description: errors[0] || t('SOMETHING_WENT_WRONG'),
                    });
                }
            },
            okButtonProps: {
                loading: actionLoading,
            },
        });
    };

    const handleAction = (action: string, document: IDocument) => {
        switch (action) {
        case 'EDIT':
            navigate(`/my-library/${document._id}`);
            break;
        case 'DELETE':
            confirmDelete(document.title, document._id || '');
            break;
        default:
            break;
        }
    };

    const COLUMNS = [
        {
            title: t('TABLE.TITLE'),
            dataIndex: 'title',
            key: 'title',
            width: 400,
        },
        {
            title: t('TABLE.TYPE'),
            dataIndex: 'type',
            key: 'type',
            width: 240,
            render: (type: string) => (
                <Text className="table__not-available">
                    {t(`TABLE.${type}`)}
                </Text>
            ),
        },
        {
            title: t('TABLE.CATEGORY'),
            dataIndex: 'categoryID',
            key: 'categoryID',
            width: 240,
            render: (categoryID: any) => {
                const categoryText = renderCategory(categoryID);

                return (
                    <Text className="table__category">
                        {categoryText}
                    </Text>
                );
            },
        },
        {
            title: (
                <div className="table__help">
                    {t('TABLE.EXTERNAL_LINK')}
                    <Tooltip
                        placement="bottom"
                        showArrow
                        title={t('HELPS.EXTERNAL_LINK')}
                    >
                        <BiHelpCircle size={16} />
                    </Tooltip>
                </div>
            ),
            dataIndex: 'externalLink',
            key: 'externalLink',
            width: 200,
            render: (externalLink: string) => (
                !externalLink
                    ? (
                        <Text className="table__not-available">
                            {t('NOT_AVAILABLE')}
                        </Text>
                    )
                    : (
                        <Tooltip title={externalLink} showArrow placement="topLeft">
                            <div className="table__external-link" onClick={() => window.open(externalLink, '_blank')}>
                                {t('TABLE.GO_TO_LINK')}
                                <BiLinkExternal size={16} className="table__external-link-icon" />
                            </div>
                        </Tooltip>
                    )
            ),
        },
        {
            title: t('TABLE.DATA_SHEET'),
            children: [
                {
                    title: t('TABLE.AUTHOR'),
                    dataIndex: 'author',
                    key: 'author',
                    width: 300,
                    render: (_: any, document: IDocument) => (
                        <Text className="table__summary">
                            {document.dataSheet.author}
                        </Text>
                    ),
                },
                {
                    title: t('TABLE.SOURCE'),
                    dataIndex: 'source',
                    key: 'source',
                    width: 250,
                    render: (_: any, document: IDocument) => (
                        <Text className="table__summary">
                            {document.dataSheet.source}
                        </Text>
                    ),
                },
                {
                    title: t('TABLE.PUBLISHER'),
                    dataIndex: 'source',
                    key: 'source',
                    width: 250,
                    render: (_: any, document: IDocument) => {
                        const { publisher } = document.dataSheet;
                        return (
                            !publisher ? (
                                <Text className="table__not-available">
                                    {t('NOT_AVAILABLE')}
                                </Text>
                            ) : (
                                <Text className="table__summary">
                                    {publisher}
                                </Text>
                            )
                        );
                    },
                },
                {
                    title: t('TABLE.PUBLISHER_YEAR'),
                    dataIndex: 'source',
                    key: 'source',
                    width: 150,
                    align: 'center' as 'center',
                    render: (_: any, document: IDocument) => (
                        <Text className="table__summary">
                            {document.dataSheet.publisherYear}
                        </Text>
                    ),
                },
            ],
        },
        {
            title: t('TABLE.ACTIONS'),
            dataIndex: 'actions',
            key: 'actions',
            fixed: 'right' as 'right',
            width: 160,
            render: (actions: any, document: IDocument) => (
                <div className="table__actions">
                    <BiEdit size={28} className="table__actions-icon" onClick={() => handleAction('EDIT', document)} />
                    <MdDelete size={28} className="table__actions-icon delete" onClick={() => handleAction('DELETE', document)} />
                </div>
            ),
        },
    ];

    if (loading) return <Loading />;

    return (
        total === 0 ? (
            <EmptyData
                t={t}
                path="/my-library/new-document"
                nameButton="ADD_DOCUMENT"
            />
        ) : (
            <Table
                className="table"
                bordered
                rowKey="_id"
                loading={loading}
                columns={COLUMNS}
                dataSource={documents}
                scroll={{ x: 1500 }}
                pagination={{
                    total,
                    pageSize: limit,
                    current: page,
                    onChange: onPageChange,
                    showSizeChanger: false,
                    size: 'small',
                }}
            />
        )
    );

};

export default TableLibrary;
